import React, { useCallback, useMemo, type Ref } from 'react';
import { graphql, useFragment } from 'react-relay';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { CUSTOM_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { PLAN } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	SECTION_ITEM_RECENT,
	SECTION_ITEM_STARRED,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_PLANS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { softwarePlanTimelineRoute } from '@jira/router-routes__advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { plansDirectoryRoute } from '@jira/router-routes__directories-routes/src/plansDirectoryRoute.tsx';
import { generatePath } from '@atlassian/react-resource-router';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useHoverEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-hover-entry-point-trigger/index.tsx';
import { ReorderActionsRenderer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/reorder-actions-renderer/ReorderActionsRenderer.tsx';
import { useNestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop/nested-item-drag-and-drop';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../constants.tsx';
import type { PlanMenuItem$key } from './__generated__/PlanMenuItem.graphql';
import { asyncPlanActionMenu } from './async-plan-action-menu/asyncPlanActionMenu.entrypoint.tsx';
import { usePlanReorderingIds } from './use-plan-reordering-ids.tsx';

type Props = {
	fragmentRef: PlanMenuItem$key;
	favouriteConnectionId: string;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

const VIEW_ALL_PLANS_HREF = plansDirectoryRoute.path;

export const PlanMenuItem = ({
	fragmentRef,
	favouriteConnectionId,
	currentItems,
	currentOrder,
	item, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}: Props): any => {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<PlanMenuItem$key>(
		graphql`
			fragment PlanMenuItem on JiraPlan {
				id
				planId @required(action: THROW)
				title
				isReadOnly
				planStatus
				favouriteValue {
					isFavourite
				}
				scenario @required(action: THROW) {
					scenarioId @required(action: THROW)
				}
				defaultNavigationItem {
					... on JiraSoftwareBuiltInNavigationItem {
						url
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		fragmentRef,
	);

	const planId = data?.planId;
	const isStarred = data?.favouriteValue?.isFavourite ?? false;
	const scenarioId = data.scenario.scenarioId;
	const planTitle = data.title || '';
	const planUrl =
		data.defaultNavigationItem?.url ||
		generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });
	const isReadOnly = data.isReadOnly || false;
	const isArchived = data.planStatus === 'ARCHIVED';
	const cloudId = useCloudId();
	const activationId = useActivationId();

	const analytics = useMemo(
		() => ({
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_PLANS],
			level: 1,
			componentType: CUSTOM_ITEM,
			itemId: PLAN,
			planId: planId ?? undefined,
			// TODO BLU-5544 send section via a single <ContextualAnalyticsData instead (which wraps the Starred or Recent panel). Use static object or useMemo, not to have rerendering issues.
			section: isStarred ? SECTION_ITEM_STARRED : SECTION_ITEM_RECENT,
		}),
		[planId, isStarred],
	);

	const entryPointParams = useMemo(
		() => ({
			planId,
			scenarioId,
			planTitle,
			isReadOnly,
			isArchived,
			isStarred,
			isUsedForSidebarNav: true,
			favouriteConnectionId,
			cloudId,
			activationId,
		}),
		[
			planId,
			scenarioId,
			planTitle,
			isReadOnly,
			isArchived,
			isStarred,
			favouriteConnectionId,
			cloudId,
			activationId,
		],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlanActionMenu,
		entryPointParams,
	);

	const triggerRef = useHoverEntryPointTrigger(entryPointActions);

	if (!data) {
		return null;
	}

	if (isStarred && currentItems && currentOrder && item) {
		return (
			<DraggablePlanMenuItem
				currentItems={currentItems}
				currentOrder={currentOrder}
				id={data.id}
				item={item}
				analytics={analytics}
				isStarred={isStarred}
				scenarioId={scenarioId}
				planId={planId}
				planTitle={planTitle}
				isReadOnly={isReadOnly}
				isArchived={isArchived}
				planUrl={planUrl}
				favouriteConnectionId={favouriteConnectionId}
				entryPointParams={entryPointParams}
			/>
		);
	}

	return (
		planId &&
		scenarioId && (
			<Nav4MenuLinkItem
				analytics={analytics}
				elemBefore={<RoadmapIcon label="" color="currentColor" />}
				href={planUrl || VIEW_ALL_PLANS_HREF}
				menuId={ENTITY_ID.PLAN(String(planId))}
				ref={triggerRef}
				actionsOnHover={
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="nav4-sidebar-plan-action-menu"
						packageName={PACKAGE_NAME}
						errorFallback="flag"
						teamName={TEAM_NAME}
						runtimeProps={entryPointParams}
					/>
				}
			>
				{planTitle}
			</Nav4MenuLinkItem>
		)
	);
};

type Analytics = {
	actionSubjectId: string;
	level: number;
	componentType: string;
	itemId: string;
	planId: number;
	section: string;
};

type EntryPointParams = {
	planId: number;
	scenarioId: number;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	isStarred: boolean;
	isUsedForSidebarNav: boolean;
	favouriteConnectionId: string;
	cloudId: string;
	activationId: string;
};

type DraggablePlanMenuItemProps = {
	id: string;
	analytics: Analytics;
	isStarred: boolean;
	scenarioId: number;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	planId: number;
	planUrl: string;
	favouriteConnectionId: string;
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	item: {
		id: string;
		label: string;
	};
	entryPointParams: EntryPointParams;
};

const DraggablePlanMenuItem = ({
	analytics,
	currentItems,
	currentOrder,
	id,
	item,
	planId,
	planTitle,
	planUrl,
	entryPointParams,
}: DraggablePlanMenuItemProps) => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlanActionMenu,
		entryPointParams,
	);
	const triggerRef = useHoverEntryPointTrigger(entryPointActions);
	const { entityAri, ownerAri, group } = usePlanReorderingIds();
	const dragAndDropState = useNestedItemDragAndDrop({
		entityAri,
		ownerAri,
		data: {
			id: id.toString(),
			label: planTitle,
		},
		group,
		item: undefined,
		isExpandable: false,
		isExpandOrFlyoutOpen: false,
		id,
		isDragAndDropEnabled: true,
		onExpandFlyoutToggle: () => {
			// No op for dashboard menu item
		},
	});

	const reorderActions = useCallback(
		(onClose: () => void) => (
			<ReorderActionsRenderer
				currentItems={currentItems}
				currentOrder={currentOrder}
				entityAri={entityAri}
				ownerAri={ownerAri}
				fireAnalyticsEvent={undefined}
				item={item}
				onClose={onClose}
			/>
		),
		[currentItems, currentOrder, entityAri, item, ownerAri],
	);

	const runtimeProps = useMemo(
		() => ({
			...entryPointParams,
			reorderActions,
		}),
		[entryPointParams, reorderActions],
	);

	const draggableRef: Ref<HTMLAnchorElement> = dragAndDropState?.draggableRef || null;
	return (
		<>
			<Nav4MenuLinkItem
				analytics={analytics}
				elemBefore={<RoadmapIcon label="" color="currentColor" />}
				href={planUrl || VIEW_ALL_PLANS_HREF}
				menuId={ENTITY_ID.PLAN(String(planId))}
				ref={mergeRefs(triggerRef, draggableRef)}
				actionsOnHover={
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="nav4-sidebar-plan-action-menu"
						packageName={PACKAGE_NAME}
						errorFallback="flag"
						teamName={TEAM_NAME}
						runtimeProps={runtimeProps}
					/>
				}
				visualContentRef={dragAndDropState?.dropTargetRef}
				dropIndicator={dragAndDropState?.dropIndicator}
				isDragging={dragAndDropState?.isDragging}
				hasDragIndicator
			>
				{planTitle}
			</Nav4MenuLinkItem>
			{dragAndDropState && dragAndDropState.maybeDragPreview}
		</>
	);
};
